import {
  CUSTOM_FIELD_NAME_MAX_LENGTH,
  LEGACY_COLUMN_COUNT,
  MAPPING_NAME_MAX_LENGTH,
} from './constants';
import { assertNever, CustomFieldNameProblem, RosterUploadFailure } from './types';

const MANAGER = 'Manager';
const MANAGER_PLURAL = 'Managers';
const SALES_REP = 'Sales Representative';
const SALES_REP_PLURAL = 'Sales Representatives';

const list = (items: readonly string[]): string => items.join(', ');

export const NEXT_GEN_CSV_UPLOAD_UNLOCKS: readonly string[] = [
  'Sales Innovation features (SSO and sharing) for the Storymakers they invite',
  'Manager to Storymaker relations, pairing Storymakers with their Sales Rep',
  'Field mapping from custom CSV or XLSX columns to Storyvine fields',
];

export const ROSTER_COPY = {
  page: {
    title: 'Upload CSV',
    breadcrumb: 'upload csv',
  },

  steps: {
    chooseCsv: 'Select Organization & CSV File',
    editMappings: 'Select Mapping',
    createMapping: 'Create Mapping',
    reviewAndUpload: 'Review & Upload',
  },

  actions: {
    next: 'Next',
    prev: 'Previous',
    submit: 'ADD STORYMAKER(S)',
    backToFileSelection: 'Choose a different file',
    removeFile: 'Remove file',
  },

  chooseCsv: {
    organizationLabel: 'SELECT ORGANIZATION',
    organizationPlaceholder: 'Select an Organization',
    filmingGroupsTitle: 'FILMING GROUP ACCESS',
    filmingGroupsExplanation: `Assign ${SALES_REP_PLURAL} to the appropriate Filming Group(s).`,
    filmingGroupsEmpty: 'This organization has no filming groups yet.',
    uploadTitle: 'SELECT ROSTER FILE',
    uploadExplanationMappable:
      'Follow the sample file structure and make sure the first row of your file is a header row. You will map each column to a Storyvine field in the next step, so the order of your columns does not matter.',
    uploadExplanationLegacy: `Follow the sample file structure and make sure to include a header row. ${SALES_REP_PLURAL} will receive further instructions.`,
    downloadCsvSample: 'Download CSV sample',
    downloadExcelSample: 'Download Excel sample',
    downloadSampleFailed: 'The sample file could not be downloaded. Please try again.',
  },

  editMappings: {
    mappingNameSectionTitle: 'Mapping Name',
    fieldMappingSectionTitle: 'Column Mappings',
    selectMappingOption: 'Select Mapping',
    selectMappingDescription:
      'You can select one of the previously defined mappings and continue to the next step.',
    createMappingOption: 'Create New Mapping',
    createMappingDescription:
      'Map the fields in your selected CSV file to the corresponding fields in the Storyvine system, then save the mapping for future use.',
    savedMappingPlaceholder: 'Select a mapping',
    savedMappingEmpty:
      'No mappings have been saved yet. Create a new mapping below and it will be available next time.',
    savedMappingIdBadge: (id: string): string => `#${id}`,
    savedMappingNotSelected: 'Select a saved mapping, or switch to Create New Mapping.',
    mappingNameLabel: 'Mapping Name',
    mappingNamePlaceholder: 'Give this mapping a name you will recognise later',
    automapNotice: (matchedCount: number) =>
      `${matchedCount} of your columns were mapped automatically. Check them and edit anything that looks wrong.`,
    tableCsvHeaderColumn: 'CSV Header',
    tableStoryvineColumn: 'Mapped Storyvine Values',
    customFieldNameLabel: 'Custom field name',
    customFieldNamePlaceholder: 'Name this custom field',
    targetPlaceholder: 'Select a Storyvine field',
    targetCustomFieldOption: 'Custom field',
    targetIgnoredOption: 'Do not import',
    targetRequiredMarker: 'Required',
    addAnotherMapping: 'Add another mapping',
    removeMapping: 'Remove this mapping',
  },

  mappingFit: {
    requiredMissingTitle: 'This saved mapping is missing required Storyvine fields.',
    requiredMissingBody: (fieldKeys: readonly string[]): string =>
      `These required Storyvine fields have no column in your file: ${list(
        fieldKeys
      )}. Map them before continuing, or edit the mapping to match this file.`,
    uncoveredFileTitle: 'Some columns in this file are not covered by the saved mapping.',
    uncoveredFileBody: (csvHeaders: readonly string[]): string =>
      `${list(
        csvHeaders
      )} are not in the saved mapping. Columns Storyvine recognised were mapped automatically. The rest will not be imported.`,
    absentSavedTitle: 'The saved mapping expects columns that this file does not have.',
    absentSavedBody: (csvHeaders: readonly string[]): string =>
      `${list(
        csvHeaders
      )} are named by the saved mapping but missing from your file, so they are skipped.`,
  },

  connectPairing: {
    title: `Connect ${SALES_REP_PLURAL} to ${MANAGER_PLURAL}`,
    guidance: `Pair the column that identifies each ${SALES_REP} with the column naming their ${MANAGER}. Storyvine uses the pair to build the reporting hierarchy, which is what decides who reviews whose content.`,
    emptyStateGuidance: 'Leave both empty and no hierarchy will be built.',
    tooltip: `For example, when the identity column is an employee id and the connection column is a manager employee id, each ${SALES_REP}'s ${MANAGER} is found by matching their manager number against another employee's id.`,
    identityLabel: `Identity Column (${SALES_REP})`,
    connectionLabel: `Connection Column (${MANAGER})`,
    columnPlaceholder: '-',
    sameColumnChosen: 'The identity and connection columns must be different.',
    identityRequiredWithConnection: `Pick an identity column to pair with the connection column, or clear both to skip building the ${MANAGER} hierarchy.`,
    connectionRequiredWithIdentity: `Pick a connection column to pair with the identity column, or clear both to skip building the ${MANAGER} hierarchy.`,
  },

  review: {
    previewTitle: 'Uploaded Rows',
    mappingSummaryTitle: 'Mapping Summary',
    hierarchyTitle: `Connection of ${SALES_REP_PLURAL} to ${MANAGER_PLURAL}`,
    hierarchyConnectedIntro: `Each ${SALES_REP} is identified by`,
    hierarchyConnectedLinker: `and connected to their ${MANAGER} through`,
    hierarchyConnectedOutro: `Storyvine uses this pair to decide who reviews whose content.`,
    hierarchyIdentityLabel: `Identity Column (${SALES_REP})`,
    hierarchyConnectionLabel: `Connection Column (${MANAGER})`,
    hierarchyDisconnected: `No connection between ${SALES_REP_PLURAL} and ${MANAGER_PLURAL} will be built. Every row will be imported as an independent ${SALES_REP} with no ${MANAGER}.`,
    invitationsTitle: 'Invitations',
    tableCsvHeaderColumn: 'CSV Header',
    tableStoryvineColumn: 'Mapped Storyvine Values',
    tableExampleColumn: 'Example',
    sendInvitationEmailLabel: 'Send Invitations Email',
    sendInvitationEmailExplanation: `Leave this on and every new ${SALES_REP} receives a welcome email with download instructions and a tutorial video. Turn it off and the accounts are still created, but nobody is told, so someone has to invite them later.`,
    additionalFeaturesLabel: 'Additional Features',
    additionalFeaturesTooltip:
      'Determines the set of features available for Mobile app and Dashboard users.',
    additionalFeaturesNone: 'None',
  },

  status: {
    unmapped: 'Unmapped',
    ignored: 'Ignored',
    autoMapped: 'Auto-mapped',
    willNotImport: 'will not import',
    ignoredColumnsNote: 'Columns marked Ignored will not be imported.',
  },

  warnings: {
    droppedEmptyHeaderColumns: (columns: readonly number[]): string =>
      columns.length === 1
        ? `Column ${columns[0]} had no header in this file, so it was ignored. Excel often exports trailing empty columns like this.`
        : `Columns ${list(
            columns.map(String)
          )} had no headers in this file, so they were ignored. Excel often exports trailing empty columns like this.`,
  },

  rowErrors: {
    title: (affectedRows: number, totalRows: number) =>
      `${affectedRows} of ${totalRows} rows cannot be imported.`,
    explanation:
      'Nothing is imported until every row is valid. Fix these rows in your file, then upload it again.',
    item: (rowNumber: number, csvHeader: string, reason: string) =>
      `Row ${rowNumber}, ${csvHeader}: ${reason}`,
    remainder: (hiddenCount: number) => `and ${hiddenCount} more.`,
    requiredValueMissing: (fieldKey: string) => `${fieldKey} cannot be empty.`,
    invalidEmail: 'This is not a valid email address.',
    duplicateEmail: (rowNumber: number) =>
      `This email also appears on row ${rowNumber}. Both rows would become one account.`,
  },

  submit: {
    successWithEmail: `Your import was successful. New ${SALES_REP_PLURAL} will receive a Welcome email with download instructions and a tutorial video.`,
    successWithoutEmail: 'Your import was successful. No invitation emails were sent.',
    partialSuccess: `Your import was successful, but some ${SALES_REP_PLURAL} could not be added. Please review.`,
    failure: `Couldn't add new ${SALES_REP_PLURAL}. Please review and try again.`,
    unexpected: 'Something went wrong.',
    backendNotConnected:
      'This import cannot be sent yet. The upload flow is finished but the server side of it is not connected.',
    inProgress: 'Adding…',
    unsavedChangesWarning:
      'You have an unfinished import on this page. Leave now and your column mapping is lost.',
  },

  validation: {
    organizationRequired: 'Select an organization to import into.',
    filmingGroupsRequired: 'Select at least one filming group.',
    fileRequired: 'Choose a roster file to continue.',
    mappingNameRequired: 'Give this mapping a name.',
    mappingNameTooLong: `A mapping name can be at most ${MAPPING_NAME_MAX_LENGTH} characters.`,
    mappingNameTaken: 'A saved mapping already uses this name. Choose another.',
    undecidedRow: 'Choose what to do with this column, or mark it "Do not import."',
    fixHighlightedFields: 'Please fix the highlighted fields to continue.',
  },
};

export const describeCustomFieldNameProblem = (problem: CustomFieldNameProblem): string => {
  switch (problem) {
    case 'empty':
      return 'Name this custom field, or choose a different option.';
    case 'tooLong':
      return `A custom field name can be at most ${CUSTOM_FIELD_NAME_MAX_LENGTH} characters.`;
    case 'duplicate':
      return 'Another column already uses this custom field name.';
    case 'reserved':
      return 'This is a reserved Storyvine field. Map the column to that field instead.';
    default:
      return assertNever(problem);
  }
};

export const describeRosterFailure = (failure: RosterUploadFailure): string => {
  switch (failure.kind) {
    case 'parseError':
      return failure.rowNumber === null
        ? `This file could not be read: ${failure.message}`
        : `This file could not be read at row ${failure.rowNumber}: ${failure.message}`;
    case 'emptyFile':
      return 'This file has no rows in it.';
    case 'noHeaderRow':
      return 'This file needs a header row naming each column, because the next step maps those names to Storyvine fields.';
    case 'noDataRows':
      return 'This file has a header row but no rows below it. Add a row for each Storymaker and try uploading again.';
    case 'xlsxNotSupportedInLegacy':
      return 'Excel files are only supported by the new roster mapping flow. Save this workbook as a CSV and upload it again.';
    case 'duplicateHeaders':
      return `Two columns share the same name: ${list(
        failure.headers
      )}. Rename one of them so each column can be mapped separately.`;
    case 'legacyHeaderMismatch':
      return `This file must have exactly ${LEGACY_COLUMN_COUNT} columns named ${list(
        failure.expected
      )}, in that order and matching case. It has ${list(failure.received)}.`;
    case 'legacyRowColumnCount':
      return `Row ${failure.rowNumber} has ${failure.actual} columns. Every row must have exactly ${failure.expected}.`;
    case 'requiredFieldsUnmapped':
      return `These required Storyvine fields have no column yet: ${list(failure.fieldKeys)}.`;
    case 'undecidedColumns':
      return `Decide what to do with these columns before continuing: ${list(failure.csvHeaders)}.`;
    case 'customFieldNameInvalid':
      return `${failure.csvHeader}: ${describeCustomFieldNameProblem(failure.problem)}`;
    case 'rowErrors':
      return `${failure.errors.length} rows have problems that must be fixed in the file itself.`;
    default:
      return assertNever(failure);
  }
};
